import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Avatar,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  FormHelperText,
  OutlinedInput,
  CircularProgress,
} from '@material-ui/core';
import { Redirect, withRouter, Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { useCookies } from 'react-cookie';
import { fetchMenuLinks } from '../../services/user';
import { login, setOrganization, setUser, completeNewPassword } from '../../redux/actions/user';
import store from '../../redux/store';
import SimpleSnackbar from '../../components/SimpleSnackbar';
import { isEmail } from '../../shared/utils/helpers';
import { COOKIE_OPTIONS } from '../../config';
import { KILLSHOT_ORGANIZATION_COOKIE } from '../../constants/cookies';

const Form = ({
  classes,
  onChange,
  onSubmit,
  username,
  password,
  onEmail,
  labelRef,
  isError,
  labelWidth,
  isSubmitDisabled,
  isLoading,
}) => (
  <div className={classes.paper}>
    <Avatar className={classes.avatar}>
      <LockOutlinedIcon />
    </Avatar>
    <Typography component="h1" variant="h5">
      Sign in
    </Typography>
    <form className={classes.form} onSubmit={onSubmit} noValidate>
      <FormControl className={classes.formControl} variant="outlined" error={isError}>
        <InputLabel ref={labelRef} htmlFor="component-outlined">
          Email *
        </InputLabel>
        <OutlinedInput
          onBlur={onEmail}
          labelWidth={labelWidth}
          id="component-outlined"
          value={username}
          onChange={onChange('username')}
        />
        {isError && <FormHelperText id="component-error-text">Enter a valid email</FormHelperText>}
      </FormControl>
      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        name="password"
        label="Password"
        type="password"
        id="password"
        autoComplete="current-password"
        value={password}
        onChange={onChange('password')}
      />
      <Button
        disabled={isSubmitDisabled || isLoading}
        fullWidth
        variant="contained"
        color="primary"
        className={classes.submit}
        onClick={onSubmit}
        type="submit"
      >
        Sign In
        {isLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
      </Button>
      <Grid container>
        <Grid item xs>
          <Link to="/forgot-password">Forgot password?</Link>
        </Grid>
      </Grid>
    </form>
  </div>
);

const FormNewPassword = ({ classes, onChange, onSubmit, password, isLoading }) => (
  <div className={classes.paper}>
    <Avatar className={classes.avatar}>
      <LockOutlinedIcon />
    </Avatar>
    <Typography component="h1" variant="h5">
      New Password
    </Typography>
    <form className={classes.form} onSubmit={onSubmit} noValidate>
      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        name="password"
        label="Password"
        type="password"
        id="password"
        autoComplete="current-password"
        value={password}
        onChange={onChange('password')}
      />
      <Button
        disabled={!password || isLoading}
        fullWidth
        variant="contained"
        color="primary"
        className={classes.submit}
        onClick={onSubmit}
        type="submit"
      >
        New Password
        {isLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
      </Button>
      <Grid container>
        <Grid item xs>
          <Link to="/forgot-password">Forgot password?</Link>
        </Grid>
      </Grid>
    </form>
  </div>
);

Form.propTypes = {
  labelWidth: PropTypes.number.isRequired,
  username: PropTypes.string.isRequired,
  password: PropTypes.string.isRequired,
  isError: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSubmitDisabled: PropTypes.bool.isRequired,
  labelRef: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onEmail: PropTypes.func.isRequired,
};

FormNewPassword.propTypes = {
  password: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const styles = makeStyles(theme => ({
  '@global': {
    body: {
      backgroundColor: theme.palette.type === 'light' ? theme.palette.common.white : '#424242',
    },
  },
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  formControl: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

const initialState = {
  username: '',
  password: '',
  message: '',
  isSnackbar: false,
};

const Login = ({ history, user, setUserAction }) => {
  const classes = styles();
  const [values, setValues] = useState(initialState);
  const [labelWidth, setLabelWidth] = useState(0);
  const [isError, setError] = useState(false);
  const labelRef = React.useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const isSubmitDisabled = isError || !(values.username && values.password);
  const [userResponse, setUserResponse] = useState({});
  const [isNewUser, setIsNewUser] = useState(false);
  const [, setCookie, removeCookie] = useCookies([KILLSHOT_ORGANIZATION_COOKIE]);

  useEffect(() => {
    setLabelWidth(labelRef?.current?.offsetWidth);
  }, []);

  const handleEmail = () => {
    if (!isEmail(values.username)) setError(true);
    else setError(false);
  };

  const handleChange = name => event => {
    setValues({ ...values, [name]: event.target.value });
  };

  const handleLogin = async event => {
    try {
      event.preventDefault();
      setIsLoading(true);
      let response;

      if (isNewUser) {
        response = await completeNewPassword(userResponse, values.password);
      } else {
        response = await login(values.username, values.password);
      }
      const jwtToken = response?.signInUserSession?.accessToken;
      const email = response?.signInUserSession?.idToken?.payload?.email;
      const organizationId = response?.signInUserSession?.idToken?.payload['custom:organization_id'];

      setUserResponse(response);

      if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setIsNewUser(true);
        setIsLoading(false);
        setValues(initialState);
        return;
      }

      await setUserAction({ token: jwtToken, isAuthenticated: true, email, organization_id: organizationId });

      try {
        const responseMenuLinks = await fetchMenuLinks();
        const userId = response?.signInUserSession?.idToken?.payload['cognito:username'];

        const userPayload = {
          username: userId,
          token: jwtToken,
          isAuthenticated: true,
          email,
          menuLinks: responseMenuLinks.menu_links.filter(ml => ml.name !== 'Business Summary'),
          organizationInfo: {}, // responseOrganization.organization,
          organization_id: organizationId, // cognito organization id
          organizations: responseMenuLinks?.organizations, // menu links organization ids
        };
        setUserAction(userPayload);
        if (responseMenuLinks?.organizations?.length) {
          const org = store?.getState()?.user?.organization;

          if (org?.id) {
            setOrganization(org);
            setCookie(KILLSHOT_ORGANIZATION_COOKIE, org.id, COOKIE_OPTIONS);
          } else {
            setOrganization(responseMenuLinks.organizations[0]);
            setCookie(KILLSHOT_ORGANIZATION_COOKIE, responseMenuLinks.organizations[0].id, COOKIE_OPTIONS);
          }
        } else {
          setOrganization(null);
          removeCookie(KILLSHOT_ORGANIZATION_COOKIE, COOKIE_OPTIONS);
        }
      } catch (e) {
        throw e;
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setValues({ ...values, isSnackbar: true, message: error.message });
      throw error;
    }
  };

  const handleSnackbar = () => setValues({ ...values, isSnackbar: false });

  if (user.isAuthenticated) {
    return <Redirect to={history.location.search?.replace('?', '')} />;
  }

  return (
    <Container component="main" maxWidth="xs">
      {!isNewUser ? (
        <Form
          labelWidth={labelWidth}
          username={values.username}
          password={values.password}
          isError={isError}
          isLoading={isLoading}
          isSubmitDisabled={isSubmitDisabled}
          labelRef={labelRef}
          onChange={handleChange}
          onSubmit={handleLogin}
          onEmail={handleEmail}
          classes={classes}
        />
      ) : (
        <FormNewPassword
          labelWidth={labelWidth}
          username={values.username}
          password={values.password}
          isError={isError}
          isLoading={isLoading}
          isSubmitDisabled={isSubmitDisabled}
          labelRef={labelRef}
          onChange={handleChange}
          onSubmit={handleLogin}
          onEmail={handleEmail}
          classes={classes}
        />
      )}
      <SimpleSnackbar
        snackbarOpen={values.isSnackbar}
        variant="error"
        message={values.message}
        onClose={handleSnackbar}
      />
    </Container>
  );
};

Login.propTypes = {
  history: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  setUserAction: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  user: state.user,
});

const mapDispatchToProps = dispatch => ({
  setUserAction: bindActionCreators(setUser, dispatch),
  setOrganizationAction: bindActionCreators(setOrganization, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Login));
